import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, shareReplay } from 'rxjs';
import { Command } from 'app/models/command';
import { SiteReviewService } from './site-review.service';
import { AuthService } from './auth/auth.service';
import { TemplateViewRow, TemplateViewShare, TemplateViewVisibility, UserDirectoryEntry } from 'app/models/template-view';

// ── Proc sentinels: a single row with a negative id in place of a real row ──
/** Bad session or row not found. */
export const ROW_INVALID = -1;
/** Permission denied (row also carries errorMessage). */
export const ROW_DENIED = -2;

/** True when a proc returned a sentinel (or nothing) instead of a real row. */
export function isErrorRow(row: any): boolean {
  return !row || (row.templateViewID ?? 0) < 0;
}

/** Pure diff between two share lists → the shareSave calls needed (testable). */
export function computeShareDiff(before: TemplateViewShare[], after: TemplateViewShare[]):
    { upserts: TemplateViewShare[]; removes: TemplateViewShare[] } {
  const beforeById = new Map((before || []).map(s => [s.internalContactsID, s]));
  const afterById = new Map((after || []).map(s => [s.internalContactsID, s]));
  const upserts: TemplateViewShare[] = [];
  const removes: TemplateViewShare[] = [];
  afterById.forEach((s, id) => {
    const prev = beforeById.get(id);
    if (!prev || prev.accessLevel !== s.accessLevel) upserts.push(s);
  });
  beforeById.forEach((s, id) => {
    if (!afterById.has(id)) removes.push(s);
  });
  return { upserts, removes };
}

export interface GridStateCapture {
  columnState: string;
  filterState: string;
  sortState: string;   // combined { sort, value } encoding — same as the legacy toolbar
  groupState: string;
}

export interface TemplateViewSavePayload {
  templateViewID: number;
  pageCode: string;
  templateViewName: string;
  description?: string;
  visibility?: TemplateViewVisibility;
  columnState?: string;
  filterState?: string;
  sortState?: string;
  groupState?: string;
  pivotView?: any;
  status?: string;
  templateSortOrder?: number;
  districtID?: number;
}

/**
 * Data access + permission helpers for grid template views ("Manage Views").
 * Everything rides the generic /api/cmd pipeline; the procs are the real
 * permission layer (they return { templateViewID: -2, errorMessage } when the
 * caller is not allowed).
 *
 * Command-encoder rules used here (see Command.addParameter quirks):
 * - flags are always sent as 1/0 numbers, never JS booleans
 * - @Description is pushed manually: GetParam on the API strips only the outer
 *   quotes and the value binds as a real SqlParameter, so the raw text is sent
 *   as-is (no doubling — addParameter would double only the first apostrophe
 *   and the proc only undoes that for the name param)
 */
@Injectable({ providedIn: 'root' })
export class TemplateViewService {

  constructor(private siteRev: SiteReviewService, private auth: AuthService) {}

  // ─────────────────────────────── identity helpers ───────────────────────────────

  /** Mirror of dbo.fnTemplateView_IsAdmin: 'Template Manager' team OR rank 10. */
  isTemplateAdmin(): boolean {
    const user = this.auth.currentUser || {};
    const groups: string[] = user.groups || [];
    return groups.indexOf('Template Manager') > -1 || user.userLevel?.rank === 10;
  }

  currentUserId(): number {
    return this.auth.currentUser?.internalContactsID
        || this.auth.session?.internalContactsID
        || 0;
  }

  /** Effective visibility, tolerating legacy cached rows that lack the column. */
  visibilityOf(view: any): TemplateViewVisibility {
    if (view?.visibility) return view.visibility;
    if (view?.globalView === 1) return 'Global';
    if (view?.publicView === 1) return 'Public';
    return 'Private';
  }

  canEdit(view: any): boolean {
    if (view?.canEdit !== undefined) return view.canEdit === 1;
    return view?.createdByID === this.currentUserId();
  }

  // ─────────────────────────────── commands ───────────────────────────────

  /** All views visible to the caller, every page. Always emits an array
   *  (an invalid-session sentinel row carries no pageCode, so per-page
   *  filtering drops it naturally). */
  list(): Observable<TemplateViewRow[]> {
    const cmd = new Command();
    cmd.procedure = 'cmdTemplateList';
    // Versioned-dispatcher pattern: the caller selects the proc version.
    // v2 = the enriched/permission-aware body; no Version = legacy v1.
    cmd.addParameter('Version', 2);
    return this.siteRev.command(cmd).pipe(
      map(res => (Array.isArray(res) ? res : []) as TemplateViewRow[]));
  }

  save(view: TemplateViewSavePayload): Observable<TemplateViewRow> {
    const visibility = view.visibility || 'Private';
    const cmd = new Command();
    cmd.procedure = 'cmdTemplateViewSave';
    cmd.addParameter('TemplateViewID', view.templateViewID || 0);
    cmd.addParameter('PageCode', view.pageCode);
    cmd.addParameter('TemplateViewName', view.templateViewName);
    // Layout states: omit entirely when not provided — the v2 proc preserves
    // the stored layout for NULL state params (settings-only saves must never
    // clobber the layout). Pushed raw: addParameter would double the first
    // apostrophe inside the JSON and the proc only un-doubles the name.
    if (view.groupState !== undefined) this.pushRawString(cmd, 'groupState', view.groupState);
    if (view.columnState !== undefined) this.pushRawString(cmd, 'columnState', view.columnState);
    if (view.filterState !== undefined) this.pushRawString(cmd, 'filterState', view.filterState);
    if (view.sortState !== undefined) this.pushRawString(cmd, 'sortState', view.sortState);
    cmd.addParameter('PivotView', view.pivotView === 1 || view.pivotView === true ? 1 : 0);
    cmd.addParameter('Public', visibility === 'Public' ? 1 : 0);
    cmd.addParameter('Global', visibility === 'Global' ? 1 : 0);
    cmd.addParameter('Status', view.status || 'Active');
    // Omitted = NULL on the server = preserve existing (v2 semantics)
    if (view.templateSortOrder !== undefined && view.templateSortOrder !== null) {
      cmd.addParameter('TemplateSortOrder', view.templateSortOrder);
    }
    if (view.districtID !== undefined && view.districtID !== null) {
      cmd.addParameter('DistrictID', view.districtID);
    }
    this.pushRawString(cmd, 'Description', view.description ?? '');
    cmd.addParameter('Visibility', visibility);
    cmd.addParameter('Version', 2);   // v2 = permission-enforcing body
    return this.siteRev.command(cmd).pipe(map(res => Array.isArray(res) ? res[0] : res));
  }

  /** Soft-delete (status DELETED). The proc enforces who may: owner anywhere,
   *  admin on Global/Public — never an editor, never a non-owner on Shared. */
  deleteView(view: TemplateViewRow): Observable<TemplateViewRow> {
    return this.save({
      templateViewID: view.templateViewID,
      pageCode: view.pageCode,
      templateViewName: view.templateViewName,
      description: view.description || '',
      visibility: this.visibilityOf(view),
      pivotView: view.pivotView,
      templateSortOrder: view.templateSortOrder,
      districtID: view.districtID,
      status: 'DELETED'
    });
  }

  /** "Update current view" — overwrites ONLY the saved layout. */
  updateLayout(templateViewID: number, state: GridStateCapture): Observable<TemplateViewRow> {
    const cmd = new Command();
    cmd.procedure = 'cmdTemplateViewLayoutUpdate';
    cmd.addParameter('TemplateViewID', templateViewID);
    // Raw push: layout JSON may contain apostrophes; addParameter would mangle them.
    this.pushRawString(cmd, 'columnState', state.columnState);
    this.pushRawString(cmd, 'filterState', state.filterState);
    this.pushRawString(cmd, 'sortState', state.sortState);
    this.pushRawString(cmd, 'groupState', state.groupState);
    return this.siteRev.command(cmd).pipe(map(res => Array.isArray(res) ? res[0] : res));
  }

  shareList(templateViewID: number): Observable<TemplateViewShare[]> {
    const cmd = new Command();
    cmd.procedure = 'cmdTemplateViewShareList';
    cmd.addParameter('TemplateViewID', templateViewID);
    return this.siteRev.command(cmd).pipe(map(res => this.mapShares(res)));
  }

  shareSave(templateViewID: number, internalContactsID: number,
            accessLevel: 'Viewer' | 'Editor', status: 'Active' | 'Deleted'): Observable<TemplateViewShare[]> {
    const cmd = new Command();
    cmd.procedure = 'cmdTemplateViewShareSave';
    cmd.addParameter('TemplateViewID', templateViewID);
    cmd.addParameter('InternalContactsID', internalContactsID);
    cmd.addParameter('AccessLevel', accessLevel);
    cmd.addParameter('Status', status);
    return this.siteRev.command(cmd).pipe(map(res => this.mapShares(res)));
  }

  /** Applies add/role-change/remove deltas between two share lists. */
  applyShareDiff(templateViewID: number, before: TemplateViewShare[], after: TemplateViewShare[]): Observable<any> {
    const diff = computeShareDiff(before, after);
    const calls: Observable<any>[] = [
      ...diff.upserts.map(s => this.shareSave(templateViewID, s.internalContactsID, s.accessLevel, 'Active')),
      ...diff.removes.map(s => this.shareSave(templateViewID, s.internalContactsID, s.accessLevel, 'Deleted'))
    ];
    return calls.length ? forkJoin(calls) : of(null);
  }

  setSubscription(templateViewID: number, subscribed: boolean): Observable<any> {
    const cmd = new Command();
    cmd.procedure = 'cmdTemplateViewSubscriptionSave';
    cmd.addParameter('TemplateViewID', templateViewID);
    cmd.addParameter('Subscribed', subscribed ? 1 : 0);
    return this.siteRev.command(cmd).pipe(map(res => Array.isArray(res) ? res[0] : res));
  }

  private directory$?: Observable<UserDirectoryEntry[]>;

  /** Active users for the share picker (GET /api/User = internal contacts).
   *  Cached for the session (the endpoint is slow); call prefetchDirectory()
   *  early — e.g. when Manage Views opens — so the picker is instant.
   *  The API serializes camelCase; getUserList()'s legacy FullName computation
   *  reads PascalCase props and yields the literal string "undefined undefined",
   *  so names are rebuilt here from the real fields. */
  userDirectory(): Observable<UserDirectoryEntry[]> {
    if (!this.directory$) {
      this.directory$ = this.loadDirectory().pipe(
        catchError(() => {
          this.directory$ = undefined;   // failed — let the next caller retry
          return of([] as UserDirectoryEntry[]);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.directory$;
  }

  /** Fire-and-forget warm-up of the directory cache.
   *  (Errors are already converted to [] inside userDirectory().) */
  prefetchDirectory(): void {
    this.userDirectory().subscribe();
  }

  private loadDirectory(): Observable<UserDirectoryEntry[]> {
    return this.siteRev.getUserList().pipe(
      map((users: any[]) => (users || [])
        .filter(u => (u.status ?? u.Status ?? 'Active') === 'Active')
        .map(u => {
          const first = u.firstName ?? u.FirstName ?? '';
          const last = u.lastName ?? u.LastName ?? '';
          const email = u.email ?? u.Email ?? '';
          let name = (first + ' ' + last).trim();
          if (!name) name = u.userName ?? u.UserName ?? email;
          return {
            internalContactsID: u.internalContactsID ?? u.InternalContactsID,
            name,
            email
          };
        })
        .filter(u => !!u.internalContactsID && !!u.name)
        .sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  // ─────────────────────────────── internals ───────────────────────────────

  private mapShares(res: any): TemplateViewShare[] {
    if (!Array.isArray(res)) return [];
    // The procs signal failure with a single sentinel row instead of a list.
    // Throw so subscribers hit their error paths instead of seeing an empty list.
    const sentinel = res.length > 0 ? (res[0].templateViewShareID ?? 0) : 0;
    if (sentinel < 0) {
      throw new Error(sentinel === ROW_INVALID
        ? 'Session expired — please reload the page.'
        : (res[0].errorMessage || 'Not authorized'));
    }
    return res
      .filter(r => (r.templateViewShareID ?? 0) > 0)
      .map(r => ({
        templateViewShareID: r.templateViewShareID,
        templateViewID: r.templateViewID,
        internalContactsID: r.internalContactsID,
        firstName: r.firstName,
        lastName: r.lastName,
        name: ((r.firstName || '') + ' ' + (r.lastName || '')).trim(),
        email: r.email || '',
        accessLevel: r.accessLevel === 'Editor' ? 'Editor' : 'Viewer'
      } as TemplateViewShare));
  }

  /**
   * Push a string param without addParameter's mangling. The API's GetParam
   * strips only the outer quotes and binds the rest verbatim as an SqlParameter
   * value, so no escaping is needed (or wanted) here.
   */
  private pushRawString(cmd: Command, key: string, value: string) {
    cmd.parameters.push('@' + key + "='" + (value ?? '') + "'");
  }
}
